import { useEffect, useRef, useState, type ReactNode } from 'react'
import { Button } from '@/components/ui/button'
import { parseApiErrorStatus } from '@/lib/api-errors'

type LookupQuery = {
  isPending: boolean
  isFetching: boolean
  isError: boolean
  isSuccess: boolean
  error: unknown
  refetch: () => Promise<unknown>
}

type LookupResultsProps = {
  id: string
  label: string
  query: LookupQuery
  /** What `query` currently looks up; a retry stops applying once this changes. */
  lookupKey?: unknown
  unavailableMessage?: string
  waitingForQuery?: boolean
  loadingMessage: string
  forbiddenMessage: string
  errorMessage: string
  successMessage: string
  loadingContent?: ReactNode
  children?: ReactNode
}

export function LookupResults({
  id,
  label,
  query,
  lookupKey,
  unavailableMessage,
  waitingForQuery = false,
  loadingMessage,
  forbiddenMessage,
  errorMessage,
  successMessage,
  loadingContent,
  children,
}: LookupResultsProps) {
  // A retry belongs to the lookup it was started for, so a newer lookup is not held behind it.
  const [retried, setRetried] = useState<{ lookupKey: unknown } | null>(null)
  const retrying = retried !== null && Object.is(retried.lookupKey, lookupKey)
  const region = useRef<HTMLDivElement>(null)
  const focusAfterRetry = useRef(false)
  const unavailable = unavailableMessage !== undefined
  const pending =
    !unavailable && (waitingForQuery || query.isPending || query.isFetching || retrying)
  const failed = !unavailable && !pending && query.isError
  const forbidden = parseApiErrorStatus(query.error) === 403
  const successful = !unavailable && !pending && !query.isError && query.isSuccess
  const message = unavailable
    ? unavailableMessage
    : pending
      ? loadingMessage
      : failed
        ? forbidden
          ? forbiddenMessage
          : errorMessage
        : successful
          ? successMessage
          : loadingMessage

  useEffect(() => {
    if (focusAfterRetry.current && !pending && (successful || failed || unavailable)) {
      focusAfterRetry.current = false
      region.current?.focus()
    }
  }, [failed, pending, successful, unavailable])

  return (
    <div
      id={id}
      ref={region}
      role="region"
      aria-label={label}
      aria-busy={pending}
      tabIndex={-1}
      className="space-y-3"
    >
      <p
        id={`${id}-status`}
        role={failed ? 'alert' : 'status'}
        className={failed ? 'text-sm text-destructive' : 'text-sm text-muted-foreground'}
      >
        {message}
      </p>
      {pending && loadingContent}
      {successful && children}
      {(retrying || (failed && !forbidden)) && !unavailable && (
        <Button
          type="button"
          variant="outline"
          aria-disabled={pending}
          onBlur={() => {
            focusAfterRetry.current = false
          }}
          onClick={async event => {
            if (pending) return
            focusAfterRetry.current = document.activeElement === event.currentTarget
            const retry = { lookupKey }
            setRetried(retry)
            try {
              await query.refetch()
            } finally {
              setRetried(current => (current === retry ? null : current))
            }
          }}
        >
          Retry
        </Button>
      )}
    </div>
  )
}
